import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { setModalState, modalViewsConst } from '../../../store/modalSlice';
import { fetchSchedules, fetchScheduleRuns } from '../../../store/agentScheduleSlice';
import { useScheduleSubscription } from '../../../hooks/useScheduleSubscription';
import { ReactComponent as SettingsIcon } from '../../../assets/icons/user-settings-new.svg';
import { Skeleton, SkeletonGroup } from '../../Skeleton';
import RecentSchedules from './RecentSchedules';
import PastSchedules from './PastSchedules';
import { transformScheduleToItem, type ScheduleItem, type ScheduleQuestion, type ScheduleOccurrence } from '../../../utils/scheduleTransform';

interface ScheduleHistoryProps {
  onResultSelect: (item: ScheduleItem, question: ScheduleQuestion, occurrence: ScheduleOccurrence) => void;
  projectId?: string;
  hideNewButton?: boolean;
}

const ScheduleHistory: React.FC<ScheduleHistoryProps> = ({ onResultSelect, projectId, hideNewButton }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { schedules, scheduleRuns, isLoading } = useSelector(
    (state: RootState) => state.agentSchedule
  );

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [expandedQuestionIds, setExpandedQuestionIds] = useState<string[]>([]);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string | null>(null);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isRecentExpanded, setIsRecentExpanded] = useState<boolean>(true);
  const [isPastExpanded, setIsPastExpanded] = useState<boolean>(false);

  // Subscribe to real-time run creation/updates
  useScheduleSubscription();

  // Fetch all schedules then their runs
  const refreshAll = useCallback(() => {
    console.log('[ScheduleHistory] refreshAll called');
    dispatch(fetchSchedules()).then((action) => {
      if (fetchSchedules.fulfilled.match(action)) {
        console.log(`[ScheduleHistory] Fetched ${action.payload.length} schedules, fetching runs...`);
        for (const schedule of action.payload) {
          dispatch(fetchScheduleRuns({ scheduleId: schedule.id, limit: 20 }));
        }
      } else {
        console.log('[ScheduleHistory] fetchSchedules did not fulfill', action);
      }
    });
  }, [dispatch]);

  // Fetch on mount
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // Refetch on visibility change (user switches back to tab/window)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshAll();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refreshAll]);

  // Transform real data into UI shape, filtering by projectId when scoped
  const scheduleItems: ScheduleItem[] = schedules
    .filter((s) => (projectId ? s.projectId === projectId : !s.projectId))
    .map((s) => transformScheduleToItem(s, scheduleRuns[s.id] || []));

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleExpandQuestion = (id: string) => {
    setExpandedQuestionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const markRead = (occurrenceId: string) => {
    setReadIds((prev) => new Set(prev).add(occurrenceId));
  };

  const markUnread = (occurrenceId: string) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.delete(occurrenceId);
      return next;
    });
  };

  const getAllOccurrenceIds = (item: ScheduleItem): string[] =>
    item.questions.flatMap((q) => q.occurrences.map((o) => o.id));

  const recentSchedules = scheduleItems.filter((item) =>
    getAllOccurrenceIds(item).some((oId) => !readIds.has(oId))
  );

  const pastSchedules = scheduleItems.filter((item) =>
    getAllOccurrenceIds(item).every((oId) => readIds.has(oId))
  );

  const sharedProps = {
    readIds,
    expandedIds,
    expandedQuestionIds,
    selectedOccurrenceId,
    openMenuId,
    onToggleExpand: toggleExpand,
    onToggleExpandQuestion: toggleExpandQuestion,
    onMarkRead: markRead,
    onMarkUnread: markUnread,
    onOccurrenceSelect: (item: ScheduleItem, question: ScheduleQuestion, occurrence: ScheduleOccurrence) => {
      setSelectedOccurrenceId(occurrence.id);
      onResultSelect(item, question, occurrence);
    },
    onSetOpenMenuId: setOpenMenuId,
  };

  if (isLoading && scheduleItems.length === 0) {
    return (
      <div className="min-h-0 flex-1 overflow-hidden px-2.5 pt-3">
        <SkeletonGroup count={6} label="Loading agents" className="space-y-1">
          <div className="flex items-center gap-2 px-2.5 py-2">
            <Skeleton className="h-3.5 flex-1" />
            <Skeleton className="h-3.5 w-10" radius="full" />
          </div>
        </SkeletonGroup>
      </div>
    );
  }

  const openCreateModal = () => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.createSchedule,
      properties: { __modalSize: 'md', ...(projectId && { projectId }) },
    }));
  };

  if (scheduleItems.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="eai-description-sm">No agents yet</p>
        <p className="font-inter mb-3 mt-1 text-eai-body-xs text-eai-ink-faint">Create a recurring AI agent</p>
        <button onClick={openCreateModal} className="eai-btn-outline-xs px-4">
          New agent
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2.5 pb-3 pt-3">
        <RecentSchedules
          schedules={recentSchedules}
          isExpanded={isRecentExpanded}
          onToggleExpanded={() => setIsRecentExpanded((v) => !v)}
          {...sharedProps}
        />
        <PastSchedules
          schedules={pastSchedules}
          isExpanded={isPastExpanded}
          onToggleExpanded={() => setIsPastExpanded((v) => !v)}
          {...sharedProps}
        />
      </div>

      {/* Footer */}
      <div className={`flex flex-none items-center border-t border-eai-border px-3 py-2.5 ${hideNewButton ? "justify-end" : "justify-between"}`}>
        {!hideNewButton && (
          <button
            onClick={openCreateModal}
            className="eai-btn-outline-xs"
          >
            New agent
          </button>
        )}
        <button
          onClick={() => dispatch(setModalState({ modalOpen: true, modalView: modalViewsConst.manageSchedules, properties: projectId ? { projectId, __modalSize: 'md' } : { __modalSize: 'md' } }))}
          className="eai-btn-icon"
          title="Manage agents"
        >
          <SettingsIcon className="h-3.5 w-3.5 text-eai-ink-subtle" />
        </button>
      </div>
    </div>
  );
};

export default ScheduleHistory;
